import type { ComponentProps } from 'react';
import React from 'react';

import { fg } from '@confluence/feature-gating';
import type {
	Collaborators,
	CrossJoinTargetProduct,
	CrossJoinIntegration,
} from '@atlassian/cross-join-nudges';
import {
	buildNonContextualNote,
	RequestAccessErrorFlag,
	RequestAccessPromptFlag,
	RequestAccessSuccessFlag,
	requestAccess,
} from '@atlassian/cross-join-nudges';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import {
	getFeatureVisitedCount,
	setUserOptedOut,
	snoozeFeatureFor30Days,
} from '../services/feature-visited.ts';
import {
	fireFlagDismissed,
	fireFlagShown,
	fireFlagSnoozed,
	fireJoinButtonClicked,
	fireUserOptedOut,
	getExperimentAnalytics,
} from '../services/analytics.ts';
import type { CrossJoinNudgesInput } from '../services/run-cross-join-nudges.ts';

type ConfluenceCrossJoinIntegration = Extract<
	CrossJoinIntegration,
	| 'crossJoinNudgesV3'
	| 'crossJoinNudgesConfluenceToJpd'
	| 'crossJoinNudgesConfluenceToJpdMsr'
	| 'crossJoinNudgesConfluenceToJiraMsr'
>;

type ShowNudgeFlagInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'flags' | 'createAnalyticsEvent' | 'displayName' | 'formatMessage'
> & {
	product: CrossJoinTargetProduct;
	integration: ConfluenceCrossJoinIntegration;
	userId: string;
	collaborators: Collaborators;
	pushRoute: CrossJoinNudgesInput['push'];
};

const crossJoinRoutes: Record<ConfluenceCrossJoinIntegration, string> = {
	crossJoinNudgesV3: '/jira',
	crossJoinNudgesConfluenceToJpd: '/jira/projects',
	crossJoinNudgesConfluenceToJiraMsr: '/jira',
	crossJoinNudgesConfluenceToJpdMsr: '/jira/projects',
};

export const showNudgeFlag = async ({
	product,
	integration,
	userId,
	displayName,
	cloudId,
	collaborators,
	createAnalyticsEvent,
	flags,
	pushRoute,
	formatMessage,
}: ShowNudgeFlagInput) => {
	const featureVisitedCount = getFeatureVisitedCount(integration);
	const attributes = {
		collaborators: collaborators.length,
		userId,
		featureVisitedCount,
	};
	const analytics = getExperimentAnalytics(integration, createAnalyticsEvent, {
		attributes,
	});

	const dismiss = async (id: string | number, flags: FlagsStateContainer) => {
		fireFlagDismissed(analytics);
		await flags.hideFlag(id);
	};

	const dismissAndSnooze = async (id: string | number, flags: FlagsStateContainer) => {
		await dismiss(id, flags);
		fireFlagSnoozed(analytics);
		snoozeFeatureFor30Days(integration);
	};

	const dismissAndOptout = async (id: string | number, flags: FlagsStateContainer) => {
		await dismiss(id, flags);
		fireUserOptedOut(analytics);
		setUserOptedOut(integration);
	};

	await flags.showFlag({
		customComponent: (props) => (
			<ChoreographerRequestAccessPromptFlag
				id={props.id}
				integration={integration}
				collaborators={collaborators}
				targetProduct={product}
				viewCount={featureVisitedCount}
				onDismiss={() => dismiss(props.id, flags)}
				onSnoozeAndDismiss={() => dismissAndSnooze(props.id, flags)}
				onOptoutAndDismiss={() => dismissAndOptout(props.id, flags)}
				onSubmit={async () => {
					try {
						const nonContextualNote = fg('twcg-1402-non-contextual-ra-note-gate')
							? buildNonContextualNote({
									displayName: displayName ?? null,
									userId,
									targetProduct: product,
									formatMessage,
								})
							: undefined;
						const accessStatus = await requestAccess(
							product,
							'confluence',
							integration,
							userId,
							cloudId,
							createAnalyticsEvent,
							nonContextualNote,
						);
						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							pushRoute(crossJoinRoutes[integration]);
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							await flags.hideFlag(props.id);
							await showSuccessFlag(flags);
						} else {
							await flags.hideFlag(props.id);
							await showErrorFlag(flags, product);
						}
					} catch {
						await flags.hideFlag(props.id);
						await showErrorFlag(flags, product);
					}
					fireJoinButtonClicked(analytics);
				}}
			/>
		),
	});

	fireFlagShown(analytics);
};
export const showSuccessFlag = async (flags: FlagsStateContainer) => {
	await flags.showFlag({
		customComponent: (props) => (
			<RequestAccessSuccessFlag id={props.id} onDismiss={() => flags.hideFlag(props.id)} />
		),
	});
};

export const showErrorFlag = async (
	flags: FlagsStateContainer,
	product: CrossJoinTargetProduct,
) => {
	await flags.showFlag({
		customComponent: (props) => (
			<RequestAccessErrorFlag
				id={props.id}
				targetProduct={product}
				onDismiss={() => flags.hideFlag(props.id)}
			/>
		),
	});
};

// Choreographer Wrapper around RequestAccessPromptFlag
export function ChoreographerRequestAccessPromptFlag({
	...passThrough
}: ComponentProps<typeof RequestAccessPromptFlag>) {
	const { useChoreographedRender } = useChoreographerAssets();

	const canShow = useChoreographedRender({
		messageId: 'cc-cross-join-nudge',
	});

	return canShow ? <RequestAccessPromptFlag {...passThrough} /> : null;
}
